import { CommonModule, NgOptimizedImage } from "@angular/common";
import { ModuleWithProviders, NgModule, importProvidersFrom } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogModule } from "@angular/material/dialog";
import { MatInputModule } from "@angular/material/input";
import { MatMenuModule } from "@angular/material/menu";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { FILES_UPLOAD_API_PROVIDER, SharedUiModule } from "@dtm-frontend/shared/ui";
import { LanguageCode, SharedI18nModule, getTranslocoInlineLoader } from "@dtm-frontend/shared/ui/i18n";
import { SharedToastModule } from "@dtm-frontend/shared/ui/toast";
import { TRANSLOCO_SCOPE } from "@jsverse/transloco";
import { LetModule, PushModule } from "@ngrx/component";
import { Viewer, WebMercatorProjection } from "cesium";
import { ToastContainerModule } from "ngx-toastr";
import { MapSearchModule } from "../map-search/map-search.module";
import { AZURE_MAPS_SUBSCRIPTION_KEY } from "../shared/shared-map.tokens";
import { WeatherModule } from "../weather/weather.module";
import { AzureMapsLayerComponent } from "./components/azure-maps-layer/azure-maps-layer.component";
import { BufferAreasLayerComponent } from "./components/buffer-areas-layer/buffer-areas-layer.component";
import { CesiumContextMenuWrapperComponent } from "./components/cesium-context-menu-wrapper/context-menu-wrapper.component";
import { CesiumMapProvidersDirective, DTM_CESIUM_MAP_PROVIDERS } from "./components/cesium-map/cesium-map-providers.directive";
import { CesiumMapComponent } from "./components/cesium-map/cesium-map.component";
import { CheckinAreaLayerComponent } from "./components/checkin-area-layer/checkin-area-layer.component";
import { CircularBoundaryLayerComponent } from "./components/circular-boundary-layer/circular-boundary-layer.component";
import { CoordinatesPasteAreaComponent } from "./components/coordinates-paste-area/coordinates-paste-area.component";
import { CylindersEditorComponent } from "./components/cylinders-editor/cylinders-editor.component";
import { FlightTrackerAircraftDetailsComponent } from "./components/flight-tracker-details/flight-tracker-aircraft-details.component";
import { GeotilerMapsLayerComponent } from "./components/geotiler-maps-layer/geotiler-maps-layer.component";
import { HtmlCesiumComponent } from "./components/html-cesium/html-cesium.component";
import { ManualCoordinatesInputDialogComponent } from "./components/manual-coordinates-input-dialog/manual-coordinates-input-dialog.component";
import { ManualWidthInputDialogComponent } from "./components/manual-width-input-dialog/manual-width-input-dialog.component";
import { MapActionsPanelComponent } from "./components/map-actions-panel/map-actions-panel.component";
import { MapControlsComponent } from "./components/map-controls/map-controls.component";
import { MeasuresCesiumComponent } from "./components/map-controls/measures-cesium/measures-cesium.component";
import { MapLayerProviderComponent } from "./components/map-layer-provider/map-layer-provider.component";
import { GeographicalZonesFiltersComponent } from "./components/map-layers-with-controls/controls/filters/geographical-zones-filters.component";
import { GeographicalZonesControlsComponent } from "./components/map-layers-with-controls/controls/geographical-zones-controls.component";
import { GlobalSpatialInfoSettingsComponent } from "./components/map-layers-with-controls/controls/global-spatial-info-settings/global-spatial-info-settings.component";
import { LocalSpatialInfoForDtmSettingsComponent } from "./components/map-layers-with-controls/controls/local-spatial-info-for-dtm/local-spatial-info-for-dtm-settings.component";
import { GeographicalZonesSettingsComponent } from "./components/map-layers-with-controls/controls/zone-display-settings/geographical-zones-settings.component";
import { ZoneLegendComponent } from "./components/map-layers-with-controls/controls/zone-legend/zone-legend.component";
import { KmlControlsComponent } from "./components/map-layers-with-controls/kml/kml-controls/kml-controls.component";
import { KmlImportModalComponent } from "./components/map-layers-with-controls/kml/kml-import-modal/kml-import-modal.component";
import { GeographicalZonesLayerComponent } from "./components/map-layers-with-controls/layer/geographical-zones-layer.component";
import { DtmMapLayersWithControlsCustomElementDirective } from "./components/map-layers-with-controls/map-layers-custom.directive";
import { MapLayersSelectorComponent } from "./components/map-layers-with-controls/map-layers-selector/map-layers-selector.component";
import { MapLayersWithControlsComponent } from "./components/map-layers-with-controls/map-layers-with-controls.component";
import { MissionViewSettingsComponent } from "./components/map-layers-with-controls/mission-view-settings/mission-view-settings.component";
import { MapSearchLayerComponent } from "./components/map-layers-with-controls/poi-layer/map-search-layer.component";
import { DronePointDetailsComponent } from "./components/map-zone-info/drone-point-details/drone-point-details.component";
import { DronePointPhotoModalComponent } from "./components/map-zone-info/drone-point-photo-modal/drone-point-photo-modal.component";
import { GeographicalZonesInfoDetailsComponent } from "./components/map-zone-info/geographical-zones-info-details/geographical-zones-info-details.component";
import { GeographicalZonesInfoComponent } from "./components/map-zone-info/geographical-zones-info.component";
import { ObstaclesDtmLegendComponent } from "./components/obstacles-dtm-legend/obstacles-dtm-legend.component";
import { Polylines3DEditorComponent } from "./components/polylines3d-editor/polylines3d-editor.component";
import { PrismsEditorComponent } from "./components/prisms-editor/prisms-editor.component";
import { RiskLegendComponent } from "./components/risk-legend/risk-legend.component";
import { RouteViewLayerComponent } from "./components/route-view-layer/route-view-layer.component";
import { FlightPinComponent } from "./components/route-viewer/flight-pin/flight-pin.component";
import { RouteViewerComponent } from "./components/route-viewer/route-viewer.component";
import { RpaZoneDescriptionComponent } from "./components/rpa-zone-description/rpa-zone-description.component";
import { ContextMenuService } from "./services/context-menu.service";
import { CylindersEditorService } from "./services/entity-editors/cylinders-editor/cylinders-editor.service";
import { MapEntitiesEditorService } from "./services/entity-editors/map-entities-editor.service";
import { Polylines3DEditorService } from "./services/entity-editors/polylines3d-editor/polylines3d-editor.service";
import { PrismsEditorService } from "./services/entity-editors/prisms-editor/prisms-editor.service";
import { HeightHelperService } from "./services/height-helper.service";
import { CesiumPointerManagerDirective } from "./services/pointer-manager/cesium-pointer-manager.directive";
import { ExtractMapEntityPipe } from "./utils/extract-map-entity.pipe";

export const DEFAULT_CESIUM_VIEWER_CONFIGURATION_OPTIONS: Viewer.ConstructorOptions = {
    selectionIndicator: false,
    timeline: false,
    infoBox: false,
    fullscreenButton: false,
    baseLayerPicker: false,
    animation: false,
    shouldAnimate: false,
    skyBox: false,
    homeButton: false,
    geocoder: false,
    navigationHelpButton: false,
    terrainProvider: undefined,
    navigationInstructionsInitiallyVisible: false,
    mapMode2D: 0,
    mapProjection: new WebMercatorProjection(),
    requestRenderMode: true,
    maximumRenderTimeChange: Infinity,
    sceneModePicker: false,
    baseLayer: false,
    contextOptions: {
        webgl: { preserveDrawingBuffer: true },
    },
};

@NgModule({
    declarations: [
        CylindersEditorComponent,
        PrismsEditorComponent,
        Polylines3DEditorComponent,
        ExtractMapEntityPipe,
        CesiumPointerManagerDirective,
        RouteViewerComponent,
        MapControlsComponent,
        RouteViewLayerComponent,
        AzureMapsLayerComponent,
        GeotilerMapsLayerComponent,
        MapLayersWithControlsComponent,
        GeographicalZonesLayerComponent,
        GeographicalZonesControlsComponent,
        GeographicalZonesFiltersComponent,
        GeographicalZonesSettingsComponent,
        FlightPinComponent,
        CoordinatesPasteAreaComponent,
        MissionViewSettingsComponent,
        BufferAreasLayerComponent,
        MapActionsPanelComponent,
        ManualCoordinatesInputDialogComponent,
        GeographicalZonesInfoComponent,
        GeographicalZonesInfoDetailsComponent,
        ZoneLegendComponent,
        GlobalSpatialInfoSettingsComponent,
        LocalSpatialInfoForDtmSettingsComponent,
        CircularBoundaryLayerComponent,
        ManualWidthInputDialogComponent,
        MapLayersSelectorComponent,
        CheckinAreaLayerComponent,
        RiskLegendComponent,
        ObstaclesDtmLegendComponent,
        KmlImportModalComponent,
        KmlControlsComponent,
        MeasuresCesiumComponent,
        FlightTrackerAircraftDetailsComponent,
        HtmlCesiumComponent,
        DtmMapLayersWithControlsCustomElementDirective,
        MapSearchLayerComponent,
        CesiumMapComponent,
        MapLayerProviderComponent,
        CesiumMapProvidersDirective,
        CesiumContextMenuWrapperComponent,
        RpaZoneDescriptionComponent,
        DronePointDetailsComponent,
        DronePointPhotoModalComponent,
    ],
    imports: [
        CommonModule,
        LetModule,
        PushModule,
        SharedI18nModule,
        SharedUiModule,
        NgOptimizedImage,
        MatSlideToggleModule,
        MatInputModule,
        MatDatepickerModule,
        ReactiveFormsModule,
        MatTooltipModule,
        MatProgressBarModule,
        MatMenuModule,
        SharedToastModule.forRoot(),
        ToastContainerModule,
        MatDialogModule,
        FormsModule,
        WeatherModule,
        MapSearchModule,
    ],
    exports: [
        CylindersEditorComponent,
        PrismsEditorComponent,
        Polylines3DEditorComponent,
        ExtractMapEntityPipe,
        RouteViewerComponent,
        CesiumPointerManagerDirective,
        MapControlsComponent,
        RouteViewLayerComponent,
        AzureMapsLayerComponent,
        GeotilerMapsLayerComponent,
        MapLayersWithControlsComponent,
        GeographicalZonesLayerComponent,
        GeographicalZonesControlsComponent,
        GeographicalZonesFiltersComponent,
        FlightPinComponent,
        CoordinatesPasteAreaComponent,
        BufferAreasLayerComponent,
        MapActionsPanelComponent,
        ManualCoordinatesInputDialogComponent,
        GeographicalZonesInfoDetailsComponent,
        ZoneLegendComponent,
        GlobalSpatialInfoSettingsComponent,
        LocalSpatialInfoForDtmSettingsComponent,
        CircularBoundaryLayerComponent,
        ManualWidthInputDialogComponent,
        MapLayersSelectorComponent,
        CheckinAreaLayerComponent,
        RiskLegendComponent,
        ObstaclesDtmLegendComponent,
        KmlImportModalComponent,
        KmlControlsComponent,
        MeasuresCesiumComponent,
        FlightTrackerAircraftDetailsComponent,
        HtmlCesiumComponent,
        DtmMapLayersWithControlsCustomElementDirective,
        CesiumMapComponent,
        CesiumMapProvidersDirective,
        MapLayerProviderComponent,
        CesiumContextMenuWrapperComponent,
        RpaZoneDescriptionComponent,
        DronePointDetailsComponent,
        DronePointPhotoModalComponent,
    ],
    providers: [
        HeightHelperService,
        MapEntitiesEditorService,
        CylindersEditorService,
        PrismsEditorService,
        Polylines3DEditorService,
        ExtractMapEntityPipe,
        {
            provide: TRANSLOCO_SCOPE,
            multi: true,
            useValue: {
                scope: "dtmMapCesium",
                loader: getTranslocoInlineLoader((language: LanguageCode) => import(`./assets/i18n/${language}.json`)),
            },
        },
        {
            provide: FILES_UPLOAD_API_PROVIDER,
            useValue: null,
        },
    ],
})
export class CesiumMapModule {
    public static forTest(withContextMenuSupport: boolean = false): ModuleWithProviders<CesiumMapModule> {
        const providers: ModuleWithProviders<CesiumMapModule>["providers"] = [
            DTM_CESIUM_MAP_PROVIDERS,
            importProvidersFrom(MapSearchModule.forTest()),
            {
                provide: AZURE_MAPS_SUBSCRIPTION_KEY,
                useValue: "HS9JYfhTrM0Ud2smxePEEpkueCl81_SAD2T-VU-ZNFk",
            },
        ];

        if (withContextMenuSupport) {
            providers.push(ContextMenuService);
        }

        return {
            ngModule: CesiumMapModule,
            providers: providers,
        };
    }
}
